import { loadDeferredStyles } from '@theme/hds-load-styles';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {Promise<void> | null} */
  #hydratePromise = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #ensureCartEnhancements() {
    return window.hdsEnsureCartEnhancements?.() ?? Promise.resolve();
  }

  #handleCartAdd = async (event) => {
    if (!this.hasAttribute('auto-open')) return;
    // CartAddEvent deelt eventName met CartUpdateEvent ('cart:update'). Alleen echte
    // add-to-cart events openen de drawer — niet quantity-wijzigingen, mixed-offer
    // auto-sync op pageload, of cart-drawer-hydrate.
    if (!(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.didError) return;

    const quickAddDialog = document.getElementById('quick-add-dialog');
    if (quickAddDialog?.refs?.dialog?.open) {
      await new Promise((resolve) => {
        quickAddDialog.addEventListener(DialogCloseEvent.eventName, () => resolve(), { once: true });
      });
    }

    await this.#ensureHydratedIfNeeded();
    this.showDialog();
  };

  /**
   * @returns {Promise<void>}
   */
  #ensureHydratedIfNeeded = async () => {
    await Promise.all([loadDeferredStyles('cart'), this.#ensureCartEnhancements()]);
    if (this.dataset.cartDrawerLazy !== 'true') return;
    if (this.dataset.cartDrawerLoaded === 'true') {
      const { dialog } = this.refs;
      if (!dialog?.querySelector('cart-items-component')) {
        this.dataset.cartDrawerLoaded = 'false';
        this.setAttribute('data-cart-drawer-lazy', 'true');
        this.classList.add('cart-drawer--lazy-shell');
      } else {
        this.#scheduleDrawerSummaryRefresh(dialog);
        return;
      }
    }
    if (this.#hydratePromise) return this.#hydratePromise;

    this.#hydratePromise = this.#hydrateFromSection();
    try {
      await this.#hydratePromise;
    } finally {
      this.#hydratePromise = null;
    }
  };

  #hydrateFromSection = async () => {
    const rawId = this.dataset.headerSectionId;
    if (!rawId) return;

    const { dialog } = this.refs;
    dialog.setAttribute('aria-busy', 'true');
    this.classList.add('cart-drawer--hydrating');

    try {
      const { sectionRenderer, normalizeSectionId, buildSectionSelector } = await import('@theme/section-renderer');
      const headerNormalized = normalizeSectionId(rawId);

      const marker = document.querySelector('[data-cart-hydrate-section-id]');
      let hydrateRaw = marker?.getAttribute('data-cart-hydrate-section-id')?.trim();
      if (!hydrateRaw) {
        const fragmentSection = document
          .querySelector('.cart-drawer-hydrate--fragment-root')
          ?.closest('[id^="shopify-section-"]');
        if (fragmentSection?.id?.startsWith('shopify-section-')) {
          hydrateRaw = fragmentSection.id.slice('shopify-section-'.length);
        }
      }
      const useHydrateFragment = Boolean(hydrateRaw);
      const fetchSectionNormalized = hydrateRaw ? normalizeSectionId(hydrateRaw) : headerNormalized;

      const html = await sectionRenderer.getSectionHTML(fetchSectionNormalized, false);

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = doc.getElementById(buildSectionSelector(fetchSectionNormalized));

      if (!sectionEl) {
        throw new Error('[CartDrawer] Section wrapper not found in fetched HTML');
      }

      /** @type {HTMLDivElement | null} */
      const hydrateInner = sectionEl.querySelector('.cart-drawer-hydrate--fragment-root > .cart-drawer__inner');

      if (useHydrateFragment && hydrateInner) {
        // Fragment path: dialog krijgt dezelfde children als .cart-drawer__inner (shell houdt dialog-classes).
        while (dialog.firstChild) {
          dialog.removeChild(dialog.firstChild);
        }
        for (const child of Array.from(hydrateInner.children)) {
          dialog.appendChild(child);
        }
      } else {
        const freshDrawer = sectionEl.querySelector('cart-drawer-component');
        let sourceDialog = /** @type {HTMLDialogElement | null} */ (
          freshDrawer?.querySelector('dialog.cart-drawer__dialog')
        );

        if (!sourceDialog) {
          throw new Error(
            '[CartDrawer] No drawer markup in section response. Add the "Cart drawer hydrate" section to the header group (first in order is best) or check theme JSON.',
          );
        }

        dialog.className = sourceDialog.className;
        const labelledBy = sourceDialog.getAttribute('aria-labelledby');
        if (labelledBy) {
          dialog.setAttribute('aria-labelledby', labelledBy);
        } else {
          dialog.removeAttribute('aria-labelledby');
        }

        while (dialog.firstChild) {
          dialog.removeChild(dialog.firstChild);
        }
        for (const child of Array.from(sourceDialog.children)) {
          dialog.appendChild(child);
        }
      }

      if (useHydrateFragment && hydrateInner) {
        if (dialog.querySelector('#cart-drawer-heading-empty')) {
          dialog.classList.add('cart-drawer--empty');
        } else {
          dialog.classList.remove('cart-drawer--empty');
        }
        const heading = dialog.querySelector('#cart-drawer-heading, #cart-drawer-heading-empty');
        if (heading?.id) {
          dialog.setAttribute('aria-labelledby', heading.id);
        }
      }

      this.dataset.cartDrawerLoaded = 'true';
      this.removeAttribute('data-cart-drawer-lazy');
      this.classList.remove('cart-drawer--lazy-shell');
      this.classList.remove('cart-drawer--hydrating');

      await this.#initDeliveryEstimatesAfterHydrate();

      if (typeof this.updatedCallback === 'function') {
        this.updatedCallback();
      }

      try {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { sourceId: 'cart-drawer-hydrate' },
          }),
        );
      } catch {
        /* ignore */
      }

      this.#loadMixedOfferOnce();
    } catch {
      this.classList.remove('cart-drawer--hydrating');
    } finally {
      dialog.setAttribute('aria-busy', 'false');
    }
  };

  /**
   * @param {HTMLDialogElement | null | undefined} dialog
   */
  #scheduleDrawerSummaryRefresh = (dialog) => {
    if (!dialog) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#refreshDrawerSummaryScripts(dialog));
    });
  };

  /**
   * @param {HTMLDialogElement} dialog
   */
  #refreshDrawerSummaryScripts = (dialog) => {
    /** @type {Window & { hdsInitCartDeliveryEstimates?: (opts?: { reset?: boolean; root?: ParentNode }) => void }} */ (
      window
    ).hdsInitCartDeliveryEstimates?.({ reset: true, root: dialog });

    /** @type {Window & { hdsRefreshCartPremiumPoints?: (root?: ParentNode) => void }} */ (window).hdsRefreshCartPremiumPoints?.(
      dialog,
    );
  };

  /**
   * @returns {Promise<void>}
   */
  #waitForDeliveryEstimateApi = async () => {
    if (window.hdsInitCartDeliveryEstimates) return;

    const src = this.dataset.deliveryEstimateSrc?.trim();
    const hasGlobalScript = Boolean(
      src && document.querySelector(`script[type="module"][src="${src}"], script[src*="cart-delivery-estimate"]`),
    );

    if (!hasGlobalScript && src) {
      await new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[data-hds-delivery-estimate-loader][src="${src}"]`);
        if (existing) {
          if (existing instanceof HTMLScriptElement && existing.dataset.hdsDeliveryEstimateReady === 'true') {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => reject(new Error('delivery-estimate script')), { once: true });
          return;
        }

        const script = document.createElement('script');
        script.type = 'module';
        script.src = src;
        script.dataset.hdsDeliveryEstimateLoader = 'true';
        script.addEventListener(
          'load',
          () => {
            script.dataset.hdsDeliveryEstimateReady = 'true';
            resolve();
          },
          { once: true },
        );
        script.addEventListener('error', () => reject(new Error('delivery-estimate script')), { once: true });
        document.head.appendChild(script);
      }).catch(() => {});
    }

    for (let attempt = 0; attempt < 80 && !window.hdsInitCartDeliveryEstimates; attempt += 1) {
      await new Promise((r) => setTimeout(r, 25));
    }
  };

  #initDeliveryEstimatesAfterHydrate = async () => {
    const { dialog } = this.refs;
    await this.#waitForDeliveryEstimateApi();
    this.#scheduleDrawerSummaryRefresh(dialog);
  };

  #loadMixedOfferOnce = () => {
    const src = this.dataset.mixedOfferSrc;
    if (!src) return;

    const w = /** @type {Window & { __hdsMixedOfferCartDrawerLoaded?: boolean }} */ (window);
    if (w.__hdsMixedOfferCartDrawerLoaded) return;
    w.__hdsMixedOfferCartDrawerLoaded = true;

    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.body.appendChild(script);
    this.removeAttribute('data-mixed-offer-src');
  };

  async open() {
    await Promise.all([loadDeferredStyles('cart'), this.#ensureCartEnhancements()]);
    await this.#ensureHydratedIfNeeded();
    if (this.dataset.cartDrawerLoaded === 'true') {
      this.#scheduleDrawerSummaryRefresh(this.refs.dialog);
    }
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  if (!customElements.get('cart-drawer-component')) customElements.define('cart-drawer-component', CartDrawerComponent);
}
